import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  items: [],
  isLoading: false,
  isProcessing: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    pages: 1,
  },
  wishlistStatus: {}, // For tracking wishlist status by productId_variantId
  checkingStatus: {}, // For tracking which items are being checked
};

const wishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {
    // Set entire wishlist
    setWishlistItems(state, action) {
      state.items = action.payload;
      state.isLoading = false;
      state.error = null;
    },

    // Add item to wishlist
    addToWishlist(state, action) {
      const newItem = action.payload;
      const exists = state.items.some(
        item => item.productId === newItem.productId && 
               item.variantId === newItem.variantId
      );
      if (!exists) {
        state.items.push(newItem);
      }
      state.isProcessing = false;
    },

    // Remove item from wishlist
    removeFromWishlist(state, action) {
      const { productId, variantId } = action.payload;
      state.items = state.items.filter(
        item => !(item.productId === productId && item.variantId === variantId)
      );
      state.isProcessing = false;
    },

    // Update wishlist item
    updateWishlistItem(state, action) {
      const { productId, variantId, updates } = action.payload;
      const index = state.items.findIndex(
        item => item.productId === productId && item.variantId === variantId
      );
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...updates };
      }
    },

    // Clear wishlist
    clearWishlist(state) {
      state.items = [];
      state.isProcessing = false;
    },

    // Set loading states
    setWishlistLoading(state, action) {
      state.isLoading = action.payload;
    },

    setWishlistProcessing(state, action) {
      state.isProcessing = action.payload;
    },

    // Set error
    setWishlistError(state, action) {
      state.error = action.payload;
      state.isLoading = false;
      state.isProcessing = false;
    },

    // Set pagination
    setWishlistPagination(state, action) {
      state.pagination = action.payload;
    },

    // Set wishlist status for a specific item
    setWishlistStatus(state, action) {
      const { key, status } = action.payload;
      state.wishlistStatus[key] = status;
    },

    // Set multiple wishlist statuses
    setMultipleWishlistStatus(state, action) {
      state.wishlistStatus = { ...state.wishlistStatus, ...action.payload };
    },

    // Set checking status for a specific item
    setCheckingStatus(state, action) {
      const { key, status } = action.payload;
      state.checkingStatus[key] = status;
    },

    // Reset wishlist status for an item
    resetWishlistStatus(state, action) {
      const key = action.payload;
      delete state.wishlistStatus[key];
      delete state.checkingStatus[key];
    },

    // Merge wishlists (for after login)
    mergeWishlists(state, action) {
      const { guestItems, userItems } = action.payload;
      
      // Create a map of existing user items
      const userItemMap = new Map();
      userItems.forEach(item => {
        const key = `${item.productId}_${item.variantId || ''}`;
        userItemMap.set(key, item);
      });

      // Add guest items that don't exist in user wishlist
      guestItems.forEach(guestItem => {
        const key = `${guestItem.productId}_${guestItem.variantId || ''}`;
        if (!userItemMap.has(key)) {
          state.items.push(guestItem);
        }
      });
    },

    // Reset wishlist state
    resetWishlistState() {
      return initialState;
    },
  },
});

export const {
  setWishlistItems,
  addToWishlist,
  removeFromWishlist,
  updateWishlistItem,
  clearWishlist,
  setWishlistLoading,
  setWishlistProcessing,
  setWishlistError,
  setWishlistPagination,
  setWishlistStatus,
  setMultipleWishlistStatus,
  setCheckingStatus,
  resetWishlistStatus,
  mergeWishlists,
  resetWishlistState,
} = wishlistSlice.actions;

export default wishlistSlice.reducer;